import { useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ArrowRightLeft, Loader2, X } from 'lucide-react'
import api from '../../services/api'
import { useOpCo } from '../../context/OpCoContext'
import { workspaceNav, workspacePath } from '../../config/opcoWorkspace'
import { isUnassigned } from './OpCoBadge'

export const MOVE_BLOCKED_MESSAGE = "This request was already posted or delivered, so it can't be moved"

function moveErrorMessage(err) {
  const status = err?.response?.status
  if (status === 409) return MOVE_BLOCKED_MESSAGE
  if (status === 404) return 'This request could not be found. It may have been moved or deleted.'
  return err?.response?.data?.error || err?.response?.data?.message || 'Could not move the request. Please try again.'
}

// Picker dialog: POST /api/requests/<id>/move-opco {opco_code, reason?}.
export default function MoveOpCoDialog({ request, open, onClose, onMoved, title = 'Move to OpCo' }) {
  const { opcos } = useOpCo()
  const [code, setCode] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!open || !request) return null

  const current = (request.opco_code || '').toLowerCase()
  const choices = (opcos || []).filter((o) => o.code.toLowerCase() !== current)

  const close = () => {
    if (saving) return
    setCode('')
    setReason('')
    setError('')
    onClose?.()
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!code || saving) return
    setSaving(true)
    setError('')
    try {
      const body = { opco_code: code }
      if (reason.trim()) body.reason = reason.trim()
      const res = await api.post(`/requests/${request.id}/move-opco`, body)
      setSaving(false)
      setCode('')
      setReason('')
      onMoved?.(res?.data, code)
      onClose?.()
    } catch (err) {
      setSaving(false)
      setError(moveErrorMessage(err))
    }
  }

  // Portalled to <body>: the request header is its own stacking context, so
  // rendered in place the page's panel divider painted over the dialog.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={close} />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-opco-title"
        onSubmit={submit}
        className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 id="move-opco-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button type="button" onClick={close} aria-label="Close" className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OpCo</span>
            <select
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="">Select an OpCo...</option>
              {choices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name && o.name !== o.code ? `${o.code} - ${o.name}` : o.code}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (optional)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            />
          </label>
          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={close} className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!code || saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-navy-600 hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Move
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

// After a move: the All OpCos view still shows the request, so just refresh;
// inside a single OpCo workspace it now belongs elsewhere, so follow it there.
function useAfterMove(request, onMoved) {
  const { allOpcos } = useOpCo()
  return (data, code) => {
    if (allOpcos || !code) onMoved?.(data, code)
    else workspaceNav.assign(workspacePath(code, `/requests/${request.id}`))
  }
}

// Header button for Super Admins (OpCo admins) only; renders nothing otherwise.
export function MoveOpCoButton({ request, onMoved }) {
  const { enabled, isOpcoAdmin } = useOpCo()
  const [open, setOpen] = useState(false)
  const afterMove = useAfterMove(request, onMoved)
  if (!enabled || !request || !isOpcoAdmin) return null
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-navy-600 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-900/20 whitespace-nowrap"
      >
        <ArrowRightLeft className="h-3 w-3" />
        Move to OpCo
      </button>
      <MoveOpCoDialog request={request} open={open} onClose={() => setOpen(false)} onMoved={afterMove} />
    </>
  )
}

// Amber banner for a request with no OpCo; OpCo admins get an Assign button.
export function UnassignedOpCoBanner({ request, onMoved }) {
  const { enabled, isOpcoAdmin } = useOpCo()
  const [open, setOpen] = useState(false)
  const afterMove = useAfterMove(request, onMoved)
  if (!enabled || !request || !isUnassigned(request)) return null
  return (
    <div className="flex items-center justify-between gap-3 px-6 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300 flex-shrink-0">
      <span className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        This request has no OpCo yet — only OpCo admins can see it
      </span>
      {isOpcoAdmin && (
        <>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700"
          >
            Assign
          </button>
          <MoveOpCoDialog request={request} open={open} title="Assign to OpCo" onClose={() => setOpen(false)} onMoved={afterMove} />
        </>
      )}
    </div>
  )
}
